import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as subscriberService from '../../services/subscriber';

// The whole list is fetched once and filtered in memory (it is one row per
// signup, not a feed) — the same choice the articles grid makes. The server
// still accepts ?search=/?onlySubscribed= for the CSV export.
export const fetchSubscribers = createAsyncThunk(
    'subscribers/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            return await subscriberService.fetchSubscribers();
        } catch (error) {
            return rejectWithValue(error.response?.data?.error || error.message);
        }
    },
    {
        condition: (_, { getState }) => getState().subscribers.status !== 'loading',
    },
);

export const setSubscribed = createAsyncThunk('subscribers/setSubscribed', async ({ id, subscribed }, { rejectWithValue }) => {
    try {
        return await subscriberService.setSubscribed(id, subscribed);
    } catch (error) {
        return rejectWithValue(error.response?.data?.error || error.message);
    }
});

export const deleteSubscriber = createAsyncThunk('subscribers/delete', async (id, { rejectWithValue }) => {
    try {
        await subscriberService.deleteSubscriber(id);
        return id;
    } catch (error) {
        return rejectWithValue(error.response?.data?.error || error.message);
    }
});

export const exportSubscribers = createAsyncThunk('subscribers/export', async ({ onlySubscribed } = {}, { rejectWithValue }) => {
    try {
        await subscriberService.exportSubscribers({ onlySubscribed });
        return true;
    } catch (error) {
        return rejectWithValue(error.response?.data?.error || error.message);
    }
});

const initialState = {
    items: [],
    counts: { total: 0, subscribed: 0, unsubscribed: 0 },
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    saving: false,
    error: null,
};

// Counts come from the server with the list, but a toggle/delete answers with a
// single row — recount locally instead of refetching the whole list for a chip.
const recount = (state) => {
    state.counts = {
        total: state.items.length,
        subscribed: state.items.filter((s) => s.isSubscribed).length,
        unsubscribed: state.items.filter((s) => !s.isSubscribed).length,
    };
};

const subscriberSlice = createSlice({
    name: 'subscribers',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSubscribers.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchSubscribers.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items = action.payload.items;
                state.counts = action.payload.counts;
            })
            .addCase(fetchSubscribers.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            .addCase(setSubscribed.pending, (state) => {
                state.saving = true;
                state.error = null;
            })
            .addCase(setSubscribed.fulfilled, (state, action) => {
                state.saving = false;
                const index = state.items.findIndex((s) => s.idSubscriber === action.payload.idSubscriber);
                if (index >= 0) state.items[index] = action.payload;
                recount(state);
            })
            .addCase(setSubscribed.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload;
            })
            .addCase(deleteSubscriber.pending, (state) => {
                state.saving = true;
                state.error = null;
            })
            .addCase(deleteSubscriber.fulfilled, (state, action) => {
                state.saving = false;
                state.items = state.items.filter((s) => s.idSubscriber !== action.payload);
                recount(state);
            })
            .addCase(deleteSubscriber.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload;
            })
            .addCase(exportSubscribers.rejected, (state, action) => {
                state.error = action.payload;
            });
    },
});

export const { clearError } = subscriberSlice.actions;

export const selectAllSubscribers = (state) => state.subscribers.items;
export const selectSubscriberCounts = (state) => state.subscribers.counts;
export const selectSubscriberStatus = (state) => state.subscribers.status;
export const selectSubscriberSaving = (state) => state.subscribers.saving;
export const selectSubscriberError = (state) => state.subscribers.error;

export default subscriberSlice.reducer;
